import { Command } from 'cmdk';
import { Fragment } from 'react';

import { Tag } from '../../../cdl/Tag/Tag';
import { translate } from '../../helpers/translate.helper';
import { Characters } from '../../helpers/unicode.helper';
import { useGroupContext } from '../../hooks/useGroupContext';
import { useRole } from '../../hooks/useRole';
import { IconArrowNarrowRight } from '../../icons/cdl/ArrowNarrowRight';
import { IconPlus } from '../../icons/cdl/Plus';
import { IconSwitchHorizontal } from '../../icons/cdl/SwitchHorizontal';
import { useShortcutMenu } from '../hooks/useShortcutMenu';

import { CommandErrorState } from './CommandErrorState';
import { CommandItem } from './CommandItem';
import {
    CommandListNestedRouterPath,
    CommandListNestedRouterPathEnum,
    CommandListRouterTypeEnum,
    commandRoutes,
} from './commandRoutes';
import { CreateEnquiry } from './CreateEnquiry';
import { InviteMember } from './InviteMember';
import { Logout } from './Logout';
import { OpenChangelog } from './OpenChangelog';
import { OpenPersonalDetails } from './OpenPersonalDetails';
import { OpenSettings } from './OpenSettings';
import { OpenSupportChat } from './OpenSupportChat';
import { Search } from './Search';
import { ToggleSidebar } from './ToggleSidebar';

export const CommandListRouter = () => {
    const role = useRole();
    const { setPages, pages, setGroupContextActionType, contextActions, getContextActionHandler, setOpen } =
        useShortcutMenu();
    const { groupId, name } = useGroupContext();

    const page = pages[pages.length - 1];

    if (!page) {
        const actionRoutes = commandRoutes.filter((route) => route.path === CommandListNestedRouterPathEnum.CONTEXT);
        const enabledActionRoutes = actionRoutes.filter((route) => route.enabledForRole?.(role) ?? false);
        const openRoutes = commandRoutes
            .filter((route) => route.type === CommandListRouterTypeEnum.NESTED)
            .filter(
                (route) =>
                    route.path !== CommandListNestedRouterPathEnum.CONTEXT &&
                    route.path !== CommandListNestedRouterPathEnum.NAVIGATE &&
                    route.path !== CommandListNestedRouterPathEnum.CREATE
            )
            .filter((route) => route.enabledForRole?.(role) ?? true);

        const actionOptions = [
            {
                key: 'change-group-context',
                condition: enabledActionRoutes.length > 0,
                render: () =>
                    enabledActionRoutes.map((route) => (
                        <CommandItem
                            key={route.label}
                            Icon={IconSwitchHorizontal}
                            trailingVisual={groupId && name ? <Tag variant="default">{name}</Tag> : null}
                            onSelect={() => {
                                setGroupContextActionType(null);
                                setPages((pages) => [...pages, route.path as CommandListNestedRouterPath]);
                            }}
                        >
                            {translate('shortcutMenu.changeGroupContext')}
                        </CommandItem>
                    )),
            },
            {
                key: 'navigate-to',
                condition: true,
                render: () => (
                    <CommandItem
                        Icon={IconArrowNarrowRight}
                        onSelect={() => {
                            setPages((pages) => [...pages, CommandListNestedRouterPathEnum.NAVIGATE]);
                        }}
                    >
                        {translate('shortcutMenu.navigateTo')}
                        {Characters.HORIZONTAL_ELLIPSIS}
                    </CommandItem>
                ),
            },
            {
                key: 'open-settings',
                condition: role.hasAnyAdminRights(),
                render: () => <OpenSettings />,
            },
            {
                key: 'toggle-sidebar',
                condition: true,
                render: () => <ToggleSidebar />,
            },
            {
                key: 'create-enquiry',
                condition: role.isCustomer() && role.hasAnyCompanyWriteRights(),
                render: () => <CreateEnquiry />,
            },
            {
                key: 'create',
                condition: role.isSuperAdmin(),
                render: () => (
                    <CommandItem
                        Icon={IconPlus}
                        onSelect={() => {
                            setPages((pages) => [...pages, CommandListNestedRouterPathEnum.CREATE]);
                        }}
                    >
                        {translate('shortcutMenu.create')}
                        {Characters.HORIZONTAL_ELLIPSIS}
                    </CommandItem>
                ),
            },
            {
                key: 'invite-member',
                condition: role.isSuperAdmin(),
                render: () => <InviteMember />,
            },
            {
                key: 'open-personal-details',
                condition: !role.isSuperAdmin(),
                render: () => <OpenPersonalDetails />,
            },
            {
                key: 'open-support-chat',
                condition: !role.isSuperAdmin(),
                render: () => <OpenSupportChat />,
            },
            {
                key: 'open-changelog',
                condition: true,
                render: () => <OpenChangelog />,
            },
            {
                key: 'logout',
                condition: true,
                render: () => <Logout />,
            },
        ];
        const visibleActionOptions = actionOptions.filter((option) => option.condition);

        return (
            <Command.List>
                {contextActions.length ? (
                    <Command.Group heading={translate('shortcutMenu.contextActions')}>
                        {contextActions.map((action) => (
                            <CommandItem
                                key={action.id}
                                Icon={action.Icon}
                                destructive={action.destructive}
                                onSelect={() => {
                                    getContextActionHandler(action.id)?.();
                                    setOpen(false);
                                }}
                            >
                                {action.label}
                            </CommandItem>
                        ))}
                    </Command.Group>
                ) : null}

                {openRoutes.length ? (
                    <Command.Group heading={translate('shortcutMenu.open')}>
                        {openRoutes.map((route) => (
                            <CommandItem
                                key={route.label}
                                onSelect={() =>
                                    setPages((pages) => [...pages, route.path as CommandListNestedRouterPath])
                                }
                            >
                                {route.label}
                                {Characters.HORIZONTAL_ELLIPSIS}
                            </CommandItem>
                        ))}
                    </Command.Group>
                ) : null}

                {visibleActionOptions.length ? (
                    <Command.Group heading={translate('shortcutMenu.actions')}>
                        {visibleActionOptions.map((option) => (
                            <Fragment key={option.key}>{option.render()}</Fragment>
                        ))}
                    </Command.Group>
                ) : null}

                <Search />
            </Command.List>
        );
    }

    const matchingRoute = commandRoutes.find((route) => route.path === page);
    if (matchingRoute) {
        const Component = matchingRoute.component;
        return <Component />;
    }

    return (
        <CommandErrorState
            title={translate('shortcutMenu.invalidState.title')}
            description={translate('shortcutMenu.invalidState.description')}
        />
    );
};
